import { useState, type ReactElement } from 'react';
import { AgentBarStateLab } from './AgentBarStateLab';
import { ThinkingAuraEngine } from './ThinkingAuraEngine';
import { ThinkingAuraText } from './ThinkingAuraText';
import { DEFAULT_THINKING_AURA, THINKING_AURA_PRESETS, type AuraEngine, type AuraTheme, type ThinkingAuraSettings } from './thinkingAura';

type Key = keyof ThinkingAuraSettings;
type ColorKey = 'color1' | 'color2' | 'color3' | 'color4' | 'lightColor1' | 'lightColor2' | 'lightColor3' | 'lightColor4' | 'radialLightColor' | 'radialDarkColor' | 'textGradient1' | 'textGradient2' | 'textGradient3';

function Slider({ label, value, min, max, step, onChange }: {
  label: string; value: number; min: number; max: number; step: number; onChange: (value: number) => void;
}): ReactElement {
  return (
    <label className="thinking-aura-slider">
      <span>{label}<output>{value.toFixed(step < 0.1 ? 2 : step >= 1 ? 0 : 1)}</output></span>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(event) => onChange(Number(event.target.value))} />
    </label>
  );
}

function EngineSimulator({ engine, label, status, settings, selected, reducedMotion, theme, onSelect }: {
  engine: AuraEngine;
  label: string;
  status: string;
  settings: ThinkingAuraSettings;
  selected: boolean;
  reducedMotion: boolean;
  theme: AuraTheme;
  onSelect: () => void;
}): ReactElement {
  const previewSettings = { ...settings, enabled: true, engine };
  return (
    <div
      role="radio"
      tabIndex={0}
      className={`thinking-aura-simulator${selected ? ' thinking-aura-simulator-selected' : ''}`}
      aria-checked={selected}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
    >
      <div className="thinking-aura-simulator-head">
        <span className="thinking-aura-simulator-name">{label}</span>
        <span className="thinking-aura-simulator-state">{selected ? 'Active' : 'Select'}</span>
      </div>
      <div className="thinking-aura-simulator-row dock-row dock-row-running">
        <ThinkingAuraEngine settings={previewSettings} reducedMotion={reducedMotion} theme={theme} />
        <span className="dock-row-dots" aria-hidden="true"><span /><span /><span /></span>
        <ThinkingAuraText
          className="dock-row-status"
          settings={settings}
          reducedMotion={reducedMotion}
        >
          {status}
        </ThinkingAuraText>
      </div>
    </div>
  );
}

export function ThinkingAuraPanel({ settings, onChange, onClose, reducedMotion, previewIndex, onPreviewIndexChange, previewAction, onResetPreview }: {
  settings: ThinkingAuraSettings;
  onChange: (settings: ThinkingAuraSettings) => void;
  onClose: () => void;
  reducedMotion: boolean;
  previewIndex: number | null;
  onPreviewIndexChange: (index: number | null) => void;
  previewAction: string | null;
  onResetPreview: () => void;
}): ReactElement {
  const [paletteTheme, setPaletteTheme] = useState<AuraTheme>(() => document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark');
  const update = <K extends Key>(key: K, value: ThinkingAuraSettings[K]) => onChange({ ...settings, [key]: value });
  const colorKeys = paletteTheme === 'light'
    ? (['lightColor1', 'lightColor2', 'lightColor3', 'lightColor4'] as const)
    : (['color1', 'color2', 'color3', 'color4'] as const);
  const radialColorKey = paletteTheme === 'light' ? 'radialLightColor' : 'radialDarkColor';
  const selectedColors = colorKeys.map((key) => settings[key]);
  const activePreset = settings.engine === 'mesh'
    ? THINKING_AURA_PRESETS.find((preset) => {
        const colors = paletteTheme === 'light' ? preset.lightColors : preset.colors;
        return colors.every((value, index) => value.toLowerCase() === selectedColors[index]?.toLowerCase());
      })
    : undefined;
  const selectPreset = (colors: readonly [string, string, string, string]) => onChange({
    ...settings,
    engine: 'mesh',
    [colorKeys[0]]: colors[0],
    [colorKeys[1]]: colors[1],
    [colorKeys[2]]: colors[2],
    [colorKeys[3]]: colors[3],
  });
  const color = (key: ColorKey, label: string) => (
    <label className="thinking-aura-color" key={key}>
      <input type="color" value={settings[key]} onChange={(event) => update(key, event.target.value)} aria-label={label} />
      <span>{label}</span>
    </label>
  );
  return (
    <div className="thinking-aura-panel" role="region" aria-label="AgentBar dev controls">
      <div className="thinking-aura-panel-head">
        <div><span className="thinking-aura-eyebrow">DEV CONTROLS</span><h3>AgentBar lab</h3></div>
        <button type="button" className="thinking-aura-close" onClick={onClose} aria-label="Close AgentBar dev controls">×</button>
      </div>
      <AgentBarStateLab previewIndex={previewIndex} onPreviewIndexChange={onPreviewIndexChange} previewAction={previewAction} onResetPreview={onResetPreview} />
      <div className="thinking-aura-section-heading">
        <span className="thinking-aura-eyebrow">MESSAGE EFFECTS</span>
        <strong>Thinking aura</strong>
      </div>
      <div className="thinking-aura-simulators" role="radiogroup" aria-label="Choose an aura engine">
        <EngineSimulator
          engine="mesh"
          label="Mesh gradient"
          status="Composing the direction…"
          settings={settings}
          selected={settings.engine === 'mesh'}
          reducedMotion={reducedMotion}
          theme={paletteTheme}
          onSelect={() => update('engine', 'mesh')}
        />
        <EngineSimulator
          engine="curtain"
          label="Aurora curtain"
          status="Exploring possibilities…"
          settings={settings}
          selected={settings.engine === 'curtain'}
          reducedMotion={reducedMotion}
          theme={paletteTheme}
          onSelect={() => update('engine', 'curtain')}
        />
      </div>
      <p className="thinking-aura-preview-caption">Click a simulated thinking state to make it active.</p>

      <label className="thinking-aura-switch"><span>Enable aura</span><input type="checkbox" checked={settings.enabled} onChange={(event) => update('enabled', event.target.checked)} /></label>
      <section className="thinking-aura-controls" aria-label="Gradient controls">
        <h4>{settings.engine === 'mesh' ? 'MESH GRADIENT' : 'AURORA CURTAIN'} · COLOR &amp; MOTION</h4>
        <div className="thinking-aura-theme-row">
          <span className="thinking-aura-preset-label">EDIT PALETTE</span>
          <div className="thinking-aura-theme-toggle" role="group" aria-label="Aura color theme">
            {(['light', 'dark'] as const).map((theme) => (
              <button key={theme} type="button" aria-pressed={paletteTheme === theme} onClick={() => setPaletteTheme(theme)}>
                {theme === 'light' ? 'Light' : 'Dark'}
              </button>
            ))}
          </div>
        </div>
        <span className="thinking-aura-preset-label">{paletteTheme.toUpperCase()} PALETTES</span>
        <div className="thinking-aura-presets" aria-label="Mesh gradient color presets">
          {THINKING_AURA_PRESETS.map((preset) => {
            const colors = paletteTheme === 'light' ? preset.lightColors : preset.colors;
            return (
            <button
              key={preset.id}
              type="button"
              className={activePreset?.id === preset.id ? 'thinking-aura-preset-active' : ''}
              aria-pressed={activePreset?.id === preset.id}
              onClick={() => selectPreset(colors)}
            >
              <span className="thinking-aura-preset-swatch" style={{ background: `linear-gradient(135deg, ${colors.join(', ')})` }} />
              <span>{paletteTheme === 'light' ? preset.lightName : preset.name}</span>
            </button>
          )})}
        </div>
        <div className="thinking-aura-colors">
          {color(colorKeys[0], 'Primary')}{color(colorKeys[1], 'Secondary')}
          {settings.engine === 'mesh' ? <>{color(colorKeys[2], 'Third')}{color(colorKeys[3], 'Fourth')}</> : null}
        </div>
        <Slider label="Opacity" value={settings.opacity} min={0} max={1} step={0.01} onChange={(value) => update('opacity', value)} />
        <Slider label="Speed" value={settings.speed} min={0} max={1.5} step={0.01} onChange={(value) => update('speed', value)} />
        {settings.engine === 'mesh' ? <>
          <Slider label="Distortion" value={settings.distortion} min={0} max={1} step={0.01} onChange={(value) => update('distortion', value)} />
          <Slider label="Swirl" value={settings.swirl} min={0} max={1} step={0.01} onChange={(value) => update('swirl', value)} />
          <Slider label="Grain" value={settings.grain} min={0} max={1} step={0.01} onChange={(value) => update('grain', value)} />
          <div className="thinking-aura-subcontrols" aria-label="Static radial gradient controls">
            <div className="thinking-aura-subcontrols-head">
              <span className="thinking-aura-preset-label">STATIC RADIAL OVERLAY</span>
              <label className="thinking-aura-switch"><span>Enable</span><input type="checkbox" checked={settings.radialEnabled} onChange={(event) => update('radialEnabled', event.target.checked)} /></label>
            </div>
            {settings.radialEnabled ? <>
              <div className="thinking-aura-colors thinking-aura-radial-color">
                {color(radialColorKey, `${paletteTheme === 'light' ? 'Light' : 'Dark'} radial color`)}
              </div>
              <Slider label="Radial opacity" value={settings.radialOpacity} min={0} max={1} step={0.01} onChange={(value) => update('radialOpacity', value)} />
              <Slider label="Radius" value={settings.radialSize} min={10} max={160} step={1} onChange={(value) => update('radialSize', value)} />
              <Slider label="Horizontal position" value={settings.radialX} min={0} max={100} step={1} onChange={(value) => update('radialX', value)} />
              <Slider label="Vertical position" value={settings.radialY} min={0} max={100} step={1} onChange={(value) => update('radialY', value)} />
            </> : null}
          </div>
        </> : <>
          <Slider label="Waviness" value={settings.waviness} min={0.2} max={2} step={0.01} onChange={(value) => update('waviness', value)} />
          <Slider label="Brightness" value={settings.brightness} min={0.3} max={2} step={0.01} onChange={(value) => update('brightness', value)} />
          <label className="thinking-aura-switch"><span>Pointer tilt</span><input type="checkbox" checked={settings.interactive} onChange={(event) => update('interactive', event.target.checked)} /></label>
        </>}
      </section>
      <section className="thinking-aura-controls" aria-label="Status text controls">
        <h4>STATUS TEXT · EFFECT &amp; GRADIENT</h4>
        <label className="thinking-aura-select">Effect
          <select value={settings.textEffect} onChange={(event) => update('textEffect', event.target.value as ThinkingAuraSettings['textEffect'])}>
            <option value="none">None</option>
            <option value="shimmer">Shimmer</option>
            <option value="glow">Glow</option>
            <option value="both">Shimmer + glow</option>
          </select>
        </label>
        <Slider label="Resting opacity" value={settings.textBaseOpacity} min={0.1} max={1} step={0.01} onChange={(value) => update('textBaseOpacity', value)} />
        {settings.textEffect === 'shimmer' || settings.textEffect === 'both' ? <>
          <label className="thinking-aura-select">Highlight gradient
            <select value={settings.textGradientMode} onChange={(event) => update('textGradientMode', event.target.value as ThinkingAuraSettings['textGradientMode'])}>
              <option value="mono">Theme text color</option>
              <option value="custom">Custom colors</option>
            </select>
          </label>
          {settings.textGradientMode === 'custom' ? (
            <div className="thinking-aura-colors thinking-aura-text-colors">
              {color('textGradient1', 'Leading')}
              {color('textGradient2', 'Center')}
              {color('textGradient3', 'Trailing')}
            </div>
          ) : null}
          <Slider label="Highlight opacity" value={settings.textHighlightOpacity} min={0.1} max={1} step={0.01} onChange={(value) => update('textHighlightOpacity', value)} />
          <Slider label="Sweep duration" value={settings.textSpeed} min={1.5} max={8} step={0.1} onChange={(value) => update('textSpeed', value)} />
          <Slider label="Band width" value={settings.textBandWidth} min={0.2} max={16} step={0.1} onChange={(value) => update('textBandWidth', value)} />
          <Slider label="Edge softness" value={settings.textFeather} min={0} max={0.9} step={0.01} onChange={(value) => update('textFeather', value)} />
        </> : null}
        {settings.textEffect === 'glow' || settings.textEffect === 'both' ? (
          <Slider label="Glow strength" value={settings.textGlow} min={0} max={1} step={0.01} onChange={(value) => update('textGlow', value)} />
        ) : null}
        <Slider label="Letter spacing" value={settings.textTracking} min={-0.5} max={3} step={0.1} onChange={(value) => update('textTracking', value)} />
      </section>
      {reducedMotion ? <p className="thinking-aura-motion-note">System reduced-motion is on; animation pauses, but colors remain visible.</p> : null}
      <div className="thinking-aura-panel-foot">
        <button type="button" onClick={() => onChange({ ...DEFAULT_THINKING_AURA })}>Reset defaults</button>
        <span>Settings saved in this browser</span>
      </div>
    </div>
  );
}
